import { color, LargeBtn } from "@funded-here-interface/common";
import { Text, Modal, Image } from "@mantine/core";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStyles } from "./TwoFAQRCode.styles";
import useGenerateTwoFASK from "@funded-here-interface/common/src/hooks/useGenerateTwoFASK";
import { useDisclosure } from "@mantine/hooks";
import useVerifyTwoFAToken from "@funded-here-interface/common/src/hooks/useVerifyTwoFAToken";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setEnvironment, setToken } from "../../features/auth/authSlice_Admin";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUsers";
import phonedownload from "@funded-here-interface/common/src/assets/phonedownload.png";
import qrcodeframe from "@funded-here-interface/common/src/assets/qrcodeframe.png";
import complete2FA from "@funded-here-interface/common/src/assets/complete2FA.svg";
import {
  resetAuthState,
  setAgreedToMembershipAgreement,
  setAgreedToSpvMembershipAgreement,
  setEntityType,
  setHasCreditLimit,
  setIsAdminAuthRep,
  setIsAuthRep,
  setOnboardingType,
  setOrgId,
  setOrgName,
  setOrgRoleId,
  setRole,
  setSpvDetails,
  setSpvName,
  setInvestorToken,
  setUserEmail,
  setUserFirstName,
  setUserID,
  setUserLastName,
  setUserSpv,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import { KYCStatus } from "@funded-here-interface/common/src/constant/enum";
import { roleToPath } from "../../Utils/navigation";
import OtpField from "../../components/OtpField/OtpField";

const TwoFAQRPageCommon: FC = () => {
  const { classes } = useStyles();
  const generateTwoFASecretKeyMutation = useGenerateTwoFASK();
  const verifyTwoFATokenMutation = useVerifyTwoFAToken();
  const [openedQRCode, modalAction] = useDisclosure(false);
  const [openedSuccess, modalActionSuccess] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);
  const getUserMutation = useGetUser();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = location.state;
  const email = data.email;
  const role = data.role;
  const persona = data.persona;
  const [img, setImage] = useState("");
  const [otp, setOtp] = useState("");
  const { redirectPath } = useParams();
  const [jwtToken, setJwtToken] = useState("");

  const onChange = (value: string) => {
    setOtp(value);
  };

  const handleNext = async () => {
    if (email) {
      generateTwoFASecretKeyMutation.mutate(
        { email },
        {
          onSuccess: (data) => {
            setIsLoading(true);
            setImage(data);
            setIsLoading(false);
          },
        }
      );
    }
  };

  const handleVerifyOtp = async () => {
    if (otp !== "") {
      verifyTwoFATokenMutation.mutate(
        { token: otp, email: email.toLowerCase(), persona: persona },
        {
          onSuccess: (data) => {
            if (data) {
              setJwtToken(data);
            }
            setIsLoading(false);
            modalAction.close();
            modalActionSuccess.open();
          },
          onError: (e) => {
            if (
              (e as Error).message.includes(
                "User is not verified for two factor authentication"
              )
            ) {
              const otpError: HTMLElement | null =
                document.getElementById("otpError");
              if (otpError) {
                otpError.innerHTML =
                  "<span style='color: red;'>" +
                  "The code you entered is incorrect. Please try again.</span>";
              }
            } else {
              toast.error((e as Error).message);
            }
          },
        }
      );
    }
  };

  const handleSuccess = () => {
    sessionStorage.setItem("Auth", jwtToken);
    const token = jwtToken;
    if (role === "Admin") {
      dispatch(setToken({ token }));
      getUserMutation.mutate(
        {
          token,
        },
        {
          onSuccess: (userData) => {
            const data = userData.user;
            const role = data?.orgRole?.[0]?.role?.name;
            const roleId = data?.currentOrgRoleId;

            sessionStorage.setItem("orgRoleId", roleId);

            dispatch(setEnvironment({ env: userData.environment }));

            if (role === "product-manager") {
              navigate("/pm-dashboard/overview");
            } else if (role === "operation-and-risk") {
              navigate("/org-list");
            } else if (role === "sales-support") {
              navigate("/sales-support/new");
            } else {
              toast.error("Credentials are not valid.");
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    }
    if (role === "Investor") {
      dispatch(setInvestorToken({ token }));
      getUserMutation.mutate(
        { token },
        {
          onSuccess: (data) => {
            const userDetail = data.user;
            sessionStorage.setItem("orgRoleId", userDetail.currentOrgRoleId);

            if (userDetail?.kycStatus === KYCStatus.REJECTED) {
              toast.error("KYC Application has been Rejected.");

              return;
            }

            if (userDetail?.spvDetails?.kycStatus === KYCStatus.REJECTED) {
              toast.error("SPV KYC Application has been Rejected.");

              return;
            }

            const hasCreditLimit = userDetail?.orgRole?.[0]?.creditLimit > 0;

            dispatch(
              setAgreedToMembershipAgreement({
                agreedToMembershipAgreement:
                  userDetail?.agreedToMembershipAgreement,
              })
            );

            dispatch(
              setOrgName({
                orgName: userDetail?.orgName,
              })
            );
            dispatch(
              setAgreedToSpvMembershipAgreement({
                agreedToSpvMembershipAgreement:
                  userDetail?.agreedToSpvMembershipAgreement,
              })
            );
            dispatch(
              setHasCreditLimit({
                hasCreditLimit,
              })
            );
            dispatch(
              setIsAuthRep({
                isAuthRep: userDetail?.isAuthRep,
              })
            );
            dispatch(
              setIsAdminAuthRep({
                isAdminAuthRep: userDetail?.isAdminAuthRep,
              })
            );
            dispatch(setEntityType({ entityType: userDetail?.entityType }));
            dispatch(setUserID({ userId: userDetail?.id }));
            dispatch(setUserEmail({ userEmail: userDetail?.email }));
            dispatch(setUserSpv({ spvStatus: userDetail?.spvStatus }));
            dispatch(setSpvName({ spvName: userDetail?.spvName }));
            dispatch(
              setOnboardingType({
                type: userDetail?.pendingOnBoardingAs,
              })
            );
            dispatch(setRole({ role: userDetail?.orgRole?.[0]?.role?.name }));
            dispatch(setOrgId({ orgId: userDetail?.organization }));
            dispatch(
              setOrgRoleId({
                currentOrgRoleId: userDetail?.orgRoleId,
              })
            );
            dispatch(
              setUserFirstName({
                userFirstName: userDetail?.firstName || null,
              })
            );
            dispatch(
              setUserLastName({
                userLastName: userDetail?.lastName || null,
              })
            );
            dispatch(
              setSpvDetails({
                spvDetails: userDetail?.spvDetails || null,
              })
            );

            if (redirectPath) {
              navigate(`/${redirectPath}`);
            } else {
              const path = roleToPath(userDetail);

              if (path) {
                navigate(path);
              } else {
                dispatch(resetAuthState());
                toast.warn("No action required at this time");
              }
            }
          },
        }
      );
    }
  };

  useEffect(() => {
    if (img) {
      modalAction.open();
    }
  }, [img]);

  return (
    <div>
      <div className={classes.parentContainer}>
        <div className={classes.container}>
          <Text className={classes.headingContainer}>
            Two Factor Authentication (2FA)
          </Text>
          <hr className={classes.line} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "86%",
              marginTop: "0px",
              marginBottom: "0px",
            }}
          >
            <Text className={classes.secondheadingContainer}>
              Protect your account in just two steps!
            </Text>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "86%",
              marginTop: "0px",
              marginBottom: "0px",
            }}
          >
            <div style={{ width: "50%", marginRight: 140 }}>
              <Text className={classes.subHeading}>
                Step 1: Install an Authenticator App
              </Text>
            </div>
            <div style={{ width: "50%" }}>
              <Text className={classes.subHeading}>
                Step 2: Scan and Verify
              </Text>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "86%",
              marginTop: "0px",
              marginBottom: "0px",
            }}
          >
            <div style={{ width: "50%", marginRight: 140 }}>
              <Text className={classes.instructionHeading}>
                Download and install an authenticator app on your phone from
                Google Play or the App Store.
              </Text>
            </div>
            <div style={{ width: "50%" }}>
              <Text className={classes.instructionHeading}>
                Scan the QR code using your authenticator app and enter the
                6-digit code generated by the app to verify your 2FA setup.
              </Text>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "86%",
              marginTop: "0px",
              marginBottom: "0px",
            }}
          >
            <div style={{ width: "50%", marginRight: 140 }}>
              <div className={classes.photoDisplay}>
                <Image src={phonedownload} />
              </div>
            </div>
            <div style={{ width: "50%" }}>
              <div className={classes.photoDisplayQR}>
                <Image src={qrcodeframe} />
              </div>
              <div
                style={{
                  marginTop: "50px",
                  marginLeft: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="180" height="20">
                  <line
                    x1="0"
                    y1="10"
                    x2="180"
                    y2="10"
                    stroke="black"
                    strokeWidth="2"
                    strokeDasharray="20,10"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "86%",
              marginTop: "0px",
              marginBottom: "0px",
            }}
          >
            <div style={{ width: "50%", marginRight: 140 }}>
              <Text className={classes.instructionHeadingItalic}>
                Recommended apps:
              </Text>
              <ul className={classes.ulList}>
                <li>Google Authenticator</li>
                <li>Microsoft Authenticator</li>
              </ul>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "5px",
              marginBottom: "5px",
            }}
          >
            <LargeBtn
              onClick={handleNext}
              backgroundColor={color.FHGREEN}
              textColor="#FFFFFF"
              width="auto"
              padding="15px"
            >
              Continue to Scan QR Code
            </LargeBtn>
          </div>
        </div>
      </div>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        title="Scan and Verify"
        opened={openedQRCode}
        onClose={modalAction.close}
        size="sm"
        overlayProps={{
          blur: 3,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "5px",
            marginBottom: "5px",
          }}
        >
          <Text className={classes.instructionHeading}>
            Scan the QR Code below using your authenticator app.
          </Text>
        </div>
        {img ? (
          <div>
            <div className={classes.photoDisplay}>
              <Image src={img} />
            </div>
            <OtpField
              length={6}
              onChange={onChange}
              onEnter={handleVerifyOtp}
            />
            <div
              style={{
                textAlign: "center",
                fontSize: "10px",
                marginBottom: "20px",
              }}
            >
              <span id="otpError"></span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "5px",
                marginBottom: "5px",
              }}
            >
              <LargeBtn
                onClick={handleVerifyOtp}
                backgroundColor={color.FHGREEN}
                textColor="#FFFFFF"
                width="5%"
                disabled={otp !== "" ? false : true}
              >
                Verify
              </LargeBtn>
            </div>
          </div>
        ) : (
          <p>Loading QR code...</p>
        )}
      </Modal>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        title="Two-Factor Authentication Setup Complete"
        size="sm"
        overlayProps={{
          blur: 3,
        }}
        opened={openedSuccess}
        withCloseButton={false}
        onClose={modalActionSuccess.close}
      >
        <div className={classes.photoDisplay2FA}>
          <Image src={complete2FA} />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            marginBottom: "5px",
            textAlign: "center",
          }}
        >
          <Text className={classes.headingContainer}>2FA Enabled.</Text>
        </div>
        <div
          style={{
            display: "block",
            marginTop: "20px",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          <text className={classes.instructionHeadingNoMargin}>
            Your account is now more secure.
          </text>
          <br></br>
          <text className={classes.instructionHeadingNoMargin}>
            You will be asked for a code each time you log in.
          </text>
        </div>
        <div
          style={{
            display: "flex",
            marginTop: "30px",
            marginBottom: "10px",
            justifyContent: "center",
          }}
        >
          <LargeBtn
            onClick={handleSuccess}
            backgroundColor={color.FHGREEN}
            textColor="#FFFFFF"
            width="10"
          >
            Done
          </LargeBtn>
        </div>
      </Modal>
    </div>
  );
};

export default TwoFAQRPageCommon;
